import { createUseTranslationMap } from '@vend/react-utilities'
import { TFunction } from '@transifex/react'
import { useCallback } from 'react'
import { LSPAY_ECOM_ERROR_CODE, LSPAY_ECOM_ERRORS } from '../helpers/constants'
import { LSPayEcomErrorMessage } from '../helpers/types'

export interface ErrorData {
  errorMessage: LSPayEcomErrorMessage
  retryable: boolean
}

let lspayEcomErrorHeaders: Record<string, string>
let lspayEcomErrorBodies: Record<string, string>
const TContextHeader = 'Ecom Refund error header'
const TContextBody = 'Ecom Refund error body'

const buildErrorHeaderMap = (t: TFunction) => {
  lspayEcomErrorHeaders = {
    [LSPAY_ECOM_ERROR_CODE.DEFAULT_REFUND]: t(
      'Something went wrong and this refund has not been processed.',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ECOM_ERROR_CODE.TYPE_CLIENT_ERROR_AMOUNT_EXCEEDED]: t(
      'This refund can’t be processed',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ECOM_ERROR_CODE.TYPE_CLIENT_ERROR_ALREADY_REFUNDED]: t(
      'This refund can’t be processed',
      {
        _context: TContextHeader,
      }
    ),
    [LSPAY_ECOM_ERROR_CODE.TYPE_CLIENT_ERROR_AMOUNT_INVALID]: t(
      'This refund can’t be processed',
      {
        _context: TContextHeader,
      }
    ),
  }
}
const buildErrorBodyMap = (t: TFunction) => {
  lspayEcomErrorBodies = {
    [LSPAY_ECOM_ERROR_CODE.DEFAULT_REFUND]: t(
      'You can retry with the same payment type or choose another payment type to complete this refund. If this keeps happening please contact Lightspeed Retail Support.',
      {
        _context: TContextBody,
      }
    ),
    [LSPAY_ECOM_ERROR_CODE.TYPE_CLIENT_ERROR_AMOUNT_EXCEEDED]: t(
      'The refund amount can’t be more than the balance of this eCom sale paid with Lightspeed Payments. You can review the previous refund amount in the sales history and then try again.',
      {
        _context: TContextBody,
      }
    ),
    [LSPAY_ECOM_ERROR_CODE.TYPE_CLIENT_ERROR_ALREADY_REFUNDED]: t(
      'The original amount paid by Lightspeed Payments on this eCom sale has been fully refunded.',
      {
        _context: TContextBody,
      }
    ),
    [LSPAY_ECOM_ERROR_CODE.TYPE_CLIENT_ERROR_AMOUNT_INVALID]: t(
      'The refund amount for this eCom sale paid with Lightspeed Payments can’t be positive.',
      {
        _context: TContextBody,
      }
    ),
  }
}

const useErrorHeaderMap = createUseTranslationMap(
  buildErrorHeaderMap,
  (key: string) => lspayEcomErrorHeaders[key]
)

const useErrorBodyMap = createUseTranslationMap(
  buildErrorBodyMap,
  (key: string) => lspayEcomErrorBodies[key]
)

/**
 * Provides a function to retrieve error data (header, body, and retryable status)
 * based on a given error code.
 * Translates error messages using the translation maps.
 */

export const useErrorData = () => {
  const getHeader = useErrorHeaderMap()
  const getBody = useErrorBodyMap()

  const getErrorData = useCallback(
    (errorCode: LSPAY_ECOM_ERROR_CODE): ErrorData => {
      const isDefaultRefundCode =
        errorCode === LSPAY_ECOM_ERROR_CODE.DEFAULT_REFUND
      const resolvedErrorCode =
        isDefaultRefundCode || !LSPAY_ECOM_ERRORS.includes(errorCode)
          ? LSPAY_ECOM_ERROR_CODE.DEFAULT_REFUND
          : errorCode.toString()

      return {
        errorMessage: {
          header: getHeader(resolvedErrorCode),
          body: getBody(resolvedErrorCode),
        },
        retryable: isDefaultRefundCode,
      }
    },
    [getHeader, getBody]
  )
  return getErrorData
}
